import { useMemo, useEffect, useCallback } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from 'react-table';

// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Pagination from '@mui/material/Pagination';
import Autocomplete from '@mui/material/Autocomplete';

import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDInput from 'components/MDInput';
import CircularProgress from '@mui/material/CircularProgress';

import ReorderIcon from '@mui/icons-material/Reorder';

// Material Dashboard 2 React example components
import DataTableHeadCell from 'examples/Tables/DataTable/DataTableHeadCell';
import DataTableBodyCell from 'examples/Tables/DataTable/DataTableBodyCell';

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  totalRecords,
  perPage,
  setPerPage,
  currentPage: pageIndex,
  setCurrentPage: gotoPage,
  totalPage,
  hiddenColumns,
  queryPageSortBy,
  setSortBy,
  isLoading,
  setSearchTerm,
  onRowClick,
  isDraggable,
  onDragRow,
  isRowClickable,
}) {
  const defaultValue = entriesPerPage?.defaultValue || 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ['5', '10', '15', '20', '25'];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);
  // const noOfPages = Math.ceil(totalRecords / defaultValue);

  const pageOptions = [...new Array(totalPage).keys()];

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        //   pageIndex: pageIndex,
        //   pageSize: perPage,
        hiddenColumns,
        sortBy: queryPageSortBy,
      },
      useControlledState: (state) => {
        return useMemo(
          () => ({
            ...state,
            pageIndex: pageIndex,
            pageSize: perPage,
          }),
          [state, pageIndex, perPage]
        );
      },
      disableSortRemove: true,
      manualSortBy: true,
      manualPagination: true,
      pageCount: totalPage,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { sortBy },
  } = tableInstance;

  const onSearch = (searchValue) => {
    if (searchValue?.length > 50) {
      toast.warn(`Search Term can not be more than 50 characters`);
      return;
    }
    gotoPage(0);
    setSearchTerm(searchValue?.trim());
  };

  const debounceFn = useCallback(debounce(onSearch, 1000), []);

  const onSearchChange = (e) => {
    debounceFn(e.target.value);
  };

  useEffect(() => {
    setPerPage(perPage);
    gotoPage(0);
  }, [perPage, gotoPage]);

  useEffect(() => {
    if (sortBy?.length) {
      setSortBy(sortBy?.[0]);
      gotoPage(0);
    }
  }, [sortBy, gotoPage]);

  useEffect(() => {
    gotoPage(pageIndex);
  }, [pageIndex]);

  // Set the default value for the entries per page when component mounts
  // useEffect(() => setPerPage(defaultValue || 10), [defaultValue]);

  useEffect(() => {
    return () => {
      debounceFn.cancel();
    };
  }, [debounceFn]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPerPage(value);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? 'desc' : 'asce';
    } else if (column.disableSortBy) {
      sortedValue = 'disabled';
    } else if (isSorted) {
      sortedValue = 'none';
    } else {
      sortedValue = false;
    }
    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart =
    pageIndex === 0 ? pageIndex + 1 : pageIndex * perPage + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = perPage;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = totalRecords;
  } else {
    entriesEnd = perPage * (pageIndex + 1);
  }

  const handleDragEnd = (result, provided) => {
    if (!result?.destination) {
      return;
    }

    if (result?.destination.index === result?.source.index) {
      return;
    }

    // setLocalItems((prev: any) => {
    //   const temp = [...prev];
    //   const d = temp[result.destination!.index];
    //   temp[result.destination!.index] = temp[result.source.index];
    //   temp[result.source.index] = d;

    //   return temp;
    // });
    const draggableId = result?.draggableId;
    const destinationIndex = result?.destination?.index;
    const sourceIndex = result?.source?.index;
    onDragRow(draggableId, destinationIndex, sourceIndex);
  };

  const getTableLoader = () => {
    let colSpan = columns?.length - hiddenColumns?.length;
    if (isDraggable) {
      colSpan += 1;
    }
    return (
      <TableRow>
        <TableCell colSpan={colSpan} align="center">
          <MDBox py={5}>
            <CircularProgress color="info" />
          </MDBox>
        </TableCell>
      </TableRow>
    );
  };

  const getTableRow = (row, key) => {
    return row.cells.map((cell) => (
      <DataTableBodyCell
        noBorder={noEndBorder && row.length - 1 === key}
        align={cell.column.align ? cell.column.align : 'left'}
        {...cell.getCellProps()}
      >
        {cell.render('Cell')}
      </DataTableBodyCell>
    ));
  };

  const getEmptyRow = () => {
    return (
      <TableRow>
        <TableCell colSpan={24} align="center">
          No records to display
        </TableCell>
      </TableRow>
    );
  };

  return (
    <TableContainer sx={{ boxShadow: 'none' }}>
      {entriesPerPage || canSearch ? (
        <MDBox
          position="absolute"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          top="0"
          width="100%"
          p={3}
        >
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={perPage?.toString() ?? 10}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: '5rem' }}
                renderInput={({ inputProps, ...params }) => (
                  <MDInput
                    inputProps={{ ...inputProps, readOnly: true }}
                    {...params}
                  />
                )}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox width="12rem" ml="auto">
              <MDInput
                placeholder="Search..."
                type="search"
                // value={searchTerm}
                size="small"
                fullWidth
                onChange={onSearchChange}
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}

      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {isDraggable && <TableCell align="left" width="10ch"></TableCell>}
              {headerGroup.headers.map((column) => (
                <DataTableHeadCell
                  {...column.getHeaderProps(
                    isSorted && column.getSortByToggleProps()
                  )}
                  width={column.width ? column.width : 'auto'}
                  align={column.align ? column.align : 'left'}
                  sorted={setSortedValue(column)}
                >
                  {column.render('Header')}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        {isDraggable ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(droppableProvided) => (
                <TableBody
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  {...getTableBodyProps()}
                >
                  {isLoading ? (
                    getTableLoader()
                  ) : page?.length > 0 ? (
                    page.map((row, key) => {
                      prepareRow(row);
                      return (
                        <Draggable
                          key={key}
                          draggableId={String(row?.original?.id)}
                          index={key}
                        >
                          {(draggableProvided, snapshot) => {
                            return (
                              <TableRow
                                {...row.getRowProps()}
                                onClick={() => {
                                  onRowClick(row?.original?.id);
                                }}
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: snapshot.isDragging
                                    ? 'rgba(245,245,245, 0.75)'
                                    : 'none',
                                }}
                                sx={{
                                  cursor: isRowClickable ? 'pointer' : 'auto',
                                }}
                              >
                                <TableCell align="left">
                                  <div {...draggableProvided.dragHandleProps}>
                                    <ReorderIcon />
                                  </div>
                                </TableCell>
                                {getTableRow(row, key)}
                              </TableRow>
                            );
                          }}
                        </Draggable>
                      );
                    })
                  ) : (
                    <>{getEmptyRow()}</>
                  )}
                  {droppableProvided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <TableBody {...getTableBodyProps()}>
            {isLoading ? (
              getTableLoader()
            ) : page?.length > 0 ? (
              page.map((row, key) => {
                prepareRow(row);
                return (
                  <TableRow
                    {...row.getRowProps()}
                    onClick={() => {
                      onRowClick(row?.original?.id);
                    }}
                    sx={{
                      cursor: isRowClickable ? 'pointer' : 'auto',
                    }}
                  >
                    {getTableRow(row, key)}
                  </TableRow>
                );
              })
            ) : (
              <>{getEmptyRow()}</>
            )}
          </TableBody>
        )}
      </Table>

      {pageOptions.length > 1 && (
        <MDBox
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          sx={{
            position: 'absolute',
            width: '100%',
            marginTop: '15px',
            padding: '16px',
          }}
        >
          {showTotalEntries && (
            <MDBox mb={{ xs: 3, sm: 0 }}>
              <MDTypography
                variant="button"
                color="secondary"
                fontWeight="regular"
              >
                Showing {entriesStart} to {entriesEnd} of {totalRecords} entries
              </MDTypography>
            </MDBox>
          )}
          {pageOptions.length > 1 && (
            <Pagination
              count={pageOptions.length}
              color={pagination.color ? pagination.color : 'primary'}
              page={pageIndex + 1}
              onChange={(event, value) => {
                gotoPage(value - 1);
              }}
              variant="outlined"
              shape="circular"
              hidePrevButton={pageIndex === 0}
              hideNextButton={pageIndex === pageOptions.length - 1}
            />
          )}
        </MDBox>
      )}
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: 'gradient', color: 'info' },
  isSorted: true,
  noEndBorder: false,
  totalRecords: 0,
  setCurrentPage: () => { },
  setPerPage: () => { },
  hiddenColumns: [],
  queryPageSortBy: [],
  setSortBy: () => { },
  isLoading: false,
  onRowClick: () => { },
  isDraggable: false,
  onDragRow: () => { },
  isRowClickable: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(['contained', 'gradient']),
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'dark',
      'light',
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  totalRecords: PropTypes.number,
  setCurrentPage: PropTypes.func,
  setPerPage: PropTypes.func,
  hiddenColumns: PropTypes.array,
  queryPageSortBy: PropTypes.array,
  setSortBy: PropTypes.func,
  isLoading: PropTypes.bool,
  onRowClick: PropTypes.func,
  isDraggable: PropTypes.bool,
  onDragRow: PropTypes.func,
  isRowClickable: PropTypes.bool,
};

export default DataTable;
